import {
  LandingCTA,
  LandingDemoSection,
  LandingFeatures,
  SiteFooter,
  LandingHeader,
  LandingHero,
  LandingHowItWorks,
  LandingStickyCta
} from '@/components/landing';
import { Centered } from '@/components/styled/common.styled';
import { useSession } from '@/hooks/use-session.hook';
import { Box, CircularProgress } from '@mui/material';
import type { NextPage } from 'next';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const Home: NextPage = () => {
  const { isLoading } = useSession({ redirectIfFound: true });
  const { t } = useTranslation();

  // The page content is gated behind the session check above, so when a user
  // arrives at e.g. /#features from another page the anchor target isn't in the
  // DOM yet and the browser's one-shot native hash scroll finds nothing. Once
  // the sections mount, scroll to the hash target ourselves.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const id = window.location.hash.slice(1);
    if (!id) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading]);

  if (isLoading) {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  return (
    <React.Fragment>
      <Head>
        <title>{t('landing:indexHead.title')}</title>
        <meta name="description" content={t('landing:indexHead.description')} />
      </Head>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <LandingHeader />
        <LandingHero />
        <LandingFeatures />
        <LandingDemoSection />
        <LandingHowItWorks />
        <LandingCTA />
        <SiteFooter />
        <LandingStickyCta />
      </Box>
    </React.Fragment>
  );
};

export default Home;
